import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Dropzone from "react-dropzone";
import axios from "axios";
import useTranslations from "../../hooks/useTranslations";
import useUserLanguage from "../../hooks/useUserLanguage";

const CategoryCreate = () => {
  /* ------------------------------ ROUTE & STATE ------------------------------ */
  const { slug } = useParams();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: "",
    title: "",
    description: "",
    image: null,
  });

  const [hasParent, setHasParent] = useState(false); // Üst kategori seçilecek mi?
  const [parentId, setParentId] = useState("0");     // Seçilen üst kategori
  const [categoryList, setCategoryList] = useState([]); // Modüldeki tüm kategoriler

    const language = useUserLanguage();
    const { translations } = useTranslations(language);
    const getText = (key, defaultValue) => translations[key] || defaultValue;

  /* --------------------------- INPUT - DROPDOWN HANDLERS --------------------------- */
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDrop = (acceptedFiles) => {
    if (acceptedFiles.length) {
      setFormData((p) => ({ ...p, image: acceptedFiles[0] }));
    }
  };

  /* --------------------------- KATEGORİLERİ ÇEK --------------------------- */
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const token = localStorage.getItem("admin_token");
        const { data } = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/category/slug/${slug}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setCategoryList(data);
      } catch (err) {
        console.error("Mevcut kategoriler çekilemedi:", err);
      }
    };
    fetchCategories();
  }, [slug]);

  /* --------------------------- AĞAÇ → DÜZ SEÇENEK DİZİSİ --------------------------- */
  const parentOptions = useMemo(() => {
    // Recursive DFS
    const buildTree = (items, parent = 0, level = 0) =>
      items
        .filter((i) => i.parent === parent)
        .flatMap((i) => [
          { id: i.id, label: `${"\u2937 ".repeat(level)}${i.name}` },
          ...buildTree(items, i.id, level + 1),
        ]);

    return buildTree(categoryList);
  }, [categoryList]);

  /* --------------------------- FORM GÖNDER --------------------------- */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.image) return alert(getText("ALERT_NO_IMAGE"));

    try {
      const token = localStorage.getItem("admin_token");
      const data = new FormData();
      data.append("name", formData.name);
      data.append("title", formData.title);
      data.append("description", formData.description);
      data.append("slug", slug);
      data.append("parent", hasParent && parentId !== "0" ? parentId : "0");
      if (formData.image) data.append("image", formData.image);

      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/category`, data, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      alert(getText("ALERT_CATEGORY_CREATED"));
      navigate(`/modules/${slug}`);          // istersen ana sayfaya dön
    } catch (err) {
      console.error("Kategori oluşturma hatası:", err);
      alert(getText("ALERT_CREATE_FAILED"));
    }
  };

  /* --------------------------- RENDER --------------------------- */
  return (
    <div className="container mt-4">
      {/* ----------- Kart 1: Genel Bilgiler ----------- */}
      <div className="card p-4 shadow-sm mb-4">

        {/* Kategori Adı */}
        <div className="mb-3">
          <label className="form-label fw-bold">{getText("CATEGORY_NAME")}</label>
          <input
            className="form-control"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            required
          />
        </div>

        {/* Görsel */}
        <div className="mb-3">
          <label className="form-label fw-bold"> {getText("CATEGORY_IMAGE_OPTIONAL")}</label>
          <Dropzone onDrop={handleDrop} accept={{ "image/*": [] }} multiple={false}>
            {({ getRootProps, getInputProps }) => (
              <div
                {...getRootProps()}
                className="border p-3 text-center"
                style={{ cursor: "pointer", borderStyle: "dashed" }}
              >
                <input {...getInputProps()} />
                {formData.image ? (
                  <>
                    <img
                      src={URL.createObjectURL(formData.image)}
                      alt="Önizleme"
                      width={100}
                      className="mb-2"
                    />
                    <div>{formData.image.name}</div>
                  </>
                ) : (
                  <p>{getText("DROP_IMAGE_HINT")}</p>
                )}
              </div>
            )}
          </Dropzone>
        </div>

        {/* Üst kategori seçimi */}
        <div className="mb-3 form-check">
          <input
            type="checkbox"
            id="hasParentCheck"
            className="form-check-input"
            checked={hasParent}
            onChange={e => setHasParent(e.target.checked)}
          />
          <label htmlFor="hasParentCheck" className="form-check-label">
          {getText("WANT_PARENT_CATEGORY")}
          </label>
        </div>

        {hasParent && (
          <div className="mb-3">
            <label className="form-label fw-bold">{getText("PARENT_CATEGORY")}</label>
            <select
              className="form-select"
              value={parentId}
              onChange={e => setParentId(e.target.value)}
            >
              <option value="0">{getText("SELECT_OPTION")}</option>
              {parentOptions.map(o => (
                <option key={o.id} value={o.id}>{o.label}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* ----------- Kart 2: Meta Bilgiler ----------- */}
      <div className="card p-4 shadow-sm">

        {/* Başlık */}
        <div className="mb-3">
          <label className="form-label fw-bold">{getText("SEO_TITLE")}</label>
          <input
            className="form-control"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            required
          />
        </div>

        {/* Açıklama */}
        <div className="mb-4">
          <label className="form-label fw-bold">{getText("SEO_DESCRIPTION")}</label>
          <textarea
            className="form-control"
            name="description"
            rows={4}
            value={formData.description}
            onChange={handleInputChange}
          />
        </div>

        {/* Kaydet */}
        <div className="d-flex justify-content-center">
          <button className="btn btn-primary" onClick={handleSubmit}>
          {getText("SAVE")}
          </button>
        </div>
      </div>
    </div>
  );
};
export default CategoryCreate;
